import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import { backendApi } from "@/services/backend/api";
import {
  DeleteGfsAssessmentRequest,
  GfsAssessment,
  GfsAssessmentOverview,
  GfsAssessmentList,
  ImportGfsAssessmentBuildingRequest,
  ListGfsAssessmentsRequest,
  PutGfsAssessmentRequest,
  RefreshGfsAssessmentBuildingRequest,
  RemoveGfsAssessmentBuildingRequest,
  SetGfsAssessmentImmutableRequest,
} from "@/services/backend/gfs/assessment/types";

const gfsAssessmentApi = backendApi
  .enhanceEndpoints({ addTagTypes: ["GfsAssessment", "GfsAssessments"] })
  .injectEndpoints({
    endpoints: (build) => ({
      gfsAssessmentPut: build.mutation<GfsAssessment, PutGfsAssessmentRequest>({
        query: (body) => ({ url: "/gfs/assessments", method: "PUT", body }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsAssessment", id: request.id },
          { type: "GfsAssessments", id: "LIST" },
        ],
      }),
      gfsAssessmentDelete: build.mutation<void, DeleteGfsAssessmentRequest>({
        query: (body) => ({
          url: "/gfs/assessments",
          method: "DELETE",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsAssessment", id: request.id },
          { type: "GfsAssessments", id: "LIST" },
        ],
      }),
      gfsAssessmentImportBuilding: build.mutation<
        GfsAssessment,
        ImportGfsAssessmentBuildingRequest
      >({
        query: (body) => ({
          url: "/gfs/assessments/buildings/import",
          method: "POST",
          body,
        }),
      }),
      gfsAssessmentRefreshBuilding: build.mutation<
        GfsAssessment,
        RefreshGfsAssessmentBuildingRequest
      >({
        query: (body) => ({
          url: "/gfs/assessments/buildings/refresh",
          method: "POST",
          body,
        }),
      }),
      gfsAssessmentRemoveBuilding: build.mutation<
        GfsAssessment,
        RemoveGfsAssessmentBuildingRequest
      >({
        query: (body) => ({
          url: "/gfs/assessments/buildings",
          method: "DELETE",
          body,
        }),
      }),
      gfsAssessmentSetImmutable: build.mutation<
        GfsAssessment,
        SetGfsAssessmentImmutableRequest
      >({
        query: (body) => ({
          url: "/gfs/assessments/set-immutable",
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsAssessment", id: request.id },
          { type: "GfsAssessments", id: "LIST" },
        ],
      }),
      gfsAssessmentGet: build.query<GfsAssessment, { id: string }>({
        query: (params) => ({
          url: "/gfs/assessments/get",
          method: "GET",
          params,
        }),
        providesTags: (_result, _error, request) => [
          { type: "GfsAssessment", id: request.id },
        ],
      }),
      gfsAssessmentOverview: build.query<
        GfsAssessmentOverview,
        { assessmentId: string }
      >({
        query: (params) => ({
          url: "/gfs/assessments/overview",
          method: "GET",
          params,
        }),
        providesTags: (_result, _error, request) => [
          { type: "GfsAssessment", id: request.assessmentId },
        ],
      }),
      gfsAssessmentList: build.query<
        GfsAssessmentList,
        ListGfsAssessmentsRequest
      >({
        query: (params) => ({
          url: "/gfs/assessments/list",
          method: "GET",
          params: base64ListRequest(params),
        }),
        providesTags: [{ type: "GfsAssessments", id: "LIST" }],
      }),
    }),
  });

export const {
  useGfsAssessmentPutMutation,
  useGfsAssessmentDeleteMutation,
  useGfsAssessmentImportBuildingMutation,
  useGfsAssessmentRefreshBuildingMutation,
  useGfsAssessmentRemoveBuildingMutation,
  useGfsAssessmentSetImmutableMutation,
  useGfsAssessmentGetQuery,
  useGfsAssessmentOverviewQuery,
  useGfsAssessmentListQuery,
} = gfsAssessmentApi;
